import {
  formatE164,
  partnerCategoryLabel,
  partnerDisplayName,
  smsSegments,
  whyNotTextable,
} from "@roof-hawk/core";
import { useMemo, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { navigate, useAsync } from "../../lib/hooks";
import {
  draftBatch,
  fetchBatchMessages,
  fetchBatches,
  fetchPartners,
  markSent,
  updateBatch,
  updateMessage,
  type CampaignBatch,
  type Partner,
  type PartnerMessage,
} from "../../lib/partnerQueries";
import { SEND_CHANNEL, send } from "../../lib/sendChannel";
import { useSession } from "../../session";

/**
 * Where every message is read by a person before it goes.
 *
 * This screen is the reason the whole feature is defensible. A drip campaign
 * that drafts and sends is a marketing tool; one that drafts, waits for
 * somebody to read every message and every recipient, and only then hands the
 * approved ones to a rep's own phone, is a person keeping in touch with help
 * from a computer. The difference is entirely this queue (ADR 0067).
 *
 * So: no "approve all" without having scrolled it, no silent skipping, and the
 * count of what is left is always on screen.
 */

const when = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });

export function ReviewQueue() {
  const { isAdmin, profileId } = useSession();
  const batchesQuery = useAsync(fetchBatches, []);
  const [openBatch, setOpenBatch] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drafting, setDrafting] = useState(false);

  if (!isAdmin) {
    return (
      <div className="list">
        <div className="empty">
          <b>The review queue is Jonathon&apos;s</b>
          Every message is read before it goes out. Your own approved messages
          are on the Send screen.
        </div>
      </div>
    );
  }

  const batches = batchesQuery.data ?? [];
  const open = batches.filter(
    (batch) => batch.status === "draft" || batch.status === "in_review",
  );
  const rest = batches.filter(
    (batch) => batch.status !== "draft" && batch.status !== "in_review",
  );

  async function draftMonthly(): Promise<void> {
    setDrafting(true);
    setError(null);
    try {
      const id = await draftBatch("monthly");
      await batchesQuery.reload();
      setOpenBatch(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDrafting(false);
    }
  }

  if (openBatch) {
    return (
      <BatchReview
        batchId={openBatch}
        batch={batches.find((row) => row.id === openBatch) ?? null}
        reviewerProfileId={profileId}
        onBack={async () => {
          setOpenBatch(null);
          await batchesQuery.reload();
        }}
      />
    );
  }

  return (
    <>
      <div className="listhead">
        <h2>Review queue</h2>
        <div className="lh-right">
          <button
            className="btn-primary"
            disabled={drafting}
            onClick={() => void draftMonthly()}
          >
            {drafting ? "Drafting…" : "Draft this month's"}
          </button>
        </div>
      </div>

      <ErrorNote error={error} />

      {open.length === 0 ? (
        <div className="list">
          <div className="empty">
            <b>Nothing waiting</b>
            Storm batches appear here on their own. The monthly one can be
            drafted with the button above.
          </div>
        </div>
      ) : (
        <div className="list">
          <div className="lrow head">
            <div>Batch</div>
            <div>Drafted</div>
            <div>Approved</div>
            <div>Skipped</div>
            <div>Made</div>
            <div />
          </div>
          {open.map((batch) => (
            <div
              key={batch.id}
              className="lrow"
              onClick={() => setOpenBatch(batch.id)}
            >
              <div>
                <div className="cust">{batch.label ?? batch.kind}</div>
                <div className="sub">
                  {batch.kind === "storm" ? "Storm" : "Monthly"}
                </div>
              </div>
              <div className="sub">{batch.counts.drafted}</div>
              <div className="sub">{batch.counts.approved}</div>
              <div className="sub">{batch.counts.skipped}</div>
              <div className="sub">{when(batch.created_at)}</div>
              <div>
                <button className="btn-small">Read them</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {rest.length > 0 && (
        <>
          <div className="listhead">
            <h2>Done</h2>
          </div>
          <div className="list">
            {rest.map((batch) => (
              <div
                key={batch.id}
                className="lrow"
                onClick={() => setOpenBatch(batch.id)}
              >
                <div>
                  <div className="cust">{batch.label ?? batch.kind}</div>
                  <div className="sub">{batch.status}</div>
                </div>
                <div className="sub">{batch.counts.sent} sent</div>
                <div className="sub">{batch.counts.skipped} skipped</div>
                <div className="sub">{when(batch.created_at)}</div>
                <div />
                <div />
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

function BatchReview({
  batchId,
  batch,
  reviewerProfileId,
  onBack,
}: {
  batchId: string;
  batch: CampaignBatch | null;
  reviewerProfileId: string | null;
  onBack: () => Promise<void>;
}) {
  const messagesQuery = useAsync(() => fetchBatchMessages(batchId), [batchId]);
  const partnersQuery = useAsync(fetchPartners, []);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const partners = useMemo(() => {
    const map = new Map<string, Partner>();
    for (const partner of partnersQuery.data ?? [])
      map.set(partner.id, partner);
    return map;
  }, [partnersQuery.data]);

  const messages = messagesQuery.data ?? [];
  const waiting = messages.filter((row) => row.status === "draft");
  const done = messages.filter((row) => row.status !== "draft");

  async function act(
    id: string,
    values: Record<string, unknown>,
  ): Promise<void> {
    setError(null);
    try {
      await updateMessage(id, values);
      await messagesQuery.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function bulk(values: Record<string, unknown>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      for (const row of waiting) await updateMessage(row.id, values);
      await messagesQuery.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function release(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await updateBatch(batchId, {
        status: "approved",
        reviewed_by: reviewerProfileId,
        reviewed_at: new Date().toISOString(),
      });
      await onBack();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <>
      <div className="listhead">
        <div>
          <h2>{batch?.label ?? "Batch"}</h2>
          <div className="sub">
            {waiting.length} still to read · {done.length} dealt with
          </div>
        </div>
        <div className="lh-right">
          <button className="btn-ghost" onClick={() => void onBack()}>
            Back to the queue
          </button>
        </div>
      </div>

      <ErrorNote error={error} />

      <div className="queue-actions">
        <button
          className="btn-ghost"
          disabled={busy || waiting.length === 0}
          onClick={() => void bulk({ status: "approved" })}
        >
          Approve the remaining {waiting.length}
        </button>
        <button
          className="btn-ghost"
          disabled={busy || waiting.length === 0}
          onClick={() =>
            void bulk({
              status: "skipped",
              skip_reason: "Skipped in review, no reason given.",
            })
          }
        >
          Skip the remaining {waiting.length}
        </button>
        <button
          className="btn-primary"
          disabled={busy || waiting.length > 0}
          title={
            waiting.length > 0
              ? "Read the rest first — every message goes out from somebody's own phone."
              : "The approved messages appear in each rep's Send queue"
          }
          onClick={() => void release()}
        >
          Release to the reps
        </button>
      </div>

      {waiting.length === 0 && done.length === 0 && (
        <div className="list">
          <div className="empty">
            <b>Nothing in this batch</b>
            Everybody was either inside the rate guard or not opted in.
          </div>
        </div>
      )}

      {waiting.map((message) => (
        <DraftCard
          key={message.id}
          message={message}
          partner={partners.get(message.partner_id)}
          onApprove={() => void act(message.id, { status: "approved" })}
          onSkip={(reason) =>
            void act(message.id, { status: "skipped", skip_reason: reason })
          }
          onEdit={(body) => void act(message.id, { body })}
          reviewerProfileId={reviewerProfileId}
          onSent={() => void messagesQuery.reload()}
        />
      ))}

      {done.length > 0 && (
        <div className="list dealt">
          <div className="lrow head">
            <div>Partner</div>
            <div>What happened</div>
            <div />
          </div>
          {done.map((message) => {
            const partner = partners.get(message.partner_id);
            return (
              <div key={message.id} className="lrow">
                <div>
                  <div className="cust">
                    {partnerDisplayName(partner ?? {})}
                  </div>
                  <div className="sub">{partner?.company ?? ""}</div>
                </div>
                <div>
                  <span className={`pill ${message.status}`}>
                    {message.status}
                  </span>
                  {message.skip_reason && (
                    <span className="sub"> {message.skip_reason}</span>
                  )}
                </div>
                <div className="sub">{message.body.slice(0, 80)}</div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

/**
 * One draft, with everything needed to judge it.
 *
 * The context beside the message is not decoration: "should this person get
 * this text?" cannot be answered from the text. Who they are, when they last
 * heard from us, and what work has gone each way are what the answer is made
 * of, so they are on the card rather than a click away.
 */
function DraftCard({
  message,
  partner,
  onApprove,
  onSkip,
  onEdit,
  reviewerProfileId,
  onSent,
}: {
  message: PartnerMessage;
  partner: Partner | undefined;
  onApprove: () => void;
  onSkip: (reason: string) => void;
  onEdit: (body: string) => void;
  reviewerProfileId: string | null;
  onSent: () => void;
}) {
  const [body, setBody] = useState(message.body);
  const [skipping, setSkipping] = useState(false);
  const [reason, setReason] = useState("");
  const dirty = body !== message.body;

  const blocked = partner ? whyNotTextable(partner) : "No partner.";
  const mine = partner?.owner_profile_id === reviewerProfileId;

  async function approveAndSend(): Promise<void> {
    if (dirty) onEdit(body);
    await markSent(message.id, reviewerProfileId);
    onSent();
    send(SEND_CHANNEL, partner?.phone_mobile ?? null, body);
  }

  return (
    <div className="draft-card">
      <div className="draft-who">
        <button
          className="linky"
          onClick={() => navigate(`partner=${message.partner_id}`)}
        >
          {partnerDisplayName(partner ?? {})}
        </button>
        <div className="sub">{partner?.company ?? "—"}</div>
        <div className="sub">
          {partner ? partnerCategoryLabel(partner.category) : ""} ·{" "}
          {formatE164(partner?.phone_mobile) ?? "no mobile"}
        </div>
        <div className="sub">
          Sends from {partner?.owner?.full_name ?? "nobody"}
        </div>
        <div className="sub">
          Last texted{" "}
          {partner?.last_texted_at
            ? new Date(partner.last_texted_at).toLocaleDateString()
            : "never"}
        </div>
        {blocked && <div className="err">{blocked}</div>}
      </div>

      <div className="draft-body">
        <textarea
          rows={5}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <div className="sub">
          {body.trim().length} characters · {smsSegments(body)}{" "}
          {smsSegments(body) === 1 ? "segment" : "segments"}
          {dirty && " · edited"}
          {body.includes("{{") && (
            <span className="err"> · a merge field did not fill in</span>
          )}
        </div>

        {skipping ? (
          <div className="rowacts">
            <input
              value={reason}
              placeholder="Why not? (optional)"
              onChange={(e) => setReason(e.target.value)}
            />
            <button
              className="btn-small"
              onClick={() =>
                onSkip(reason.trim() || "Skipped in review, no reason given.")
              }
            >
              Skip
            </button>
            <button className="btn-small" onClick={() => setSkipping(false)}>
              Cancel
            </button>
          </div>
        ) : (
          <div className="rowacts">
            <button
              className="btn-primary"
              disabled={Boolean(blocked)}
              onClick={() => {
                if (dirty) onEdit(body);
                onApprove();
              }}
            >
              Approve
            </button>
            {/* For the reviewer's own partners, review and send collapse into
                one pass — there is no point queueing a message to yourself. */}
            {mine && !blocked && (
              <button
                className="btn-small"
                onClick={() => void approveAndSend()}
              >
                Approve &amp; send
              </button>
            )}
            <button className="btn-small" onClick={() => setSkipping(true)}>
              Skip
            </button>
            {dirty && (
              <button
                className="btn-small"
                onClick={() => setBody(message.body)}
              >
                Undo edit
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
